import '../../styles/_segment-progress.scss';

import React, { KeyboardEvent, useEffect, useMemo, useRef } from 'react';

import { CleTimeRecord, LaunchSegment } from '../../datasource/generated';
import { getOnDemandCreditPercentComplete } from '../../utility/creditProgress';
import { PlaybackPosition } from '../Store/playerSlice';

export type SegmentProgressStatus = 'complete' | 'in-progress' | 'not-started' | 'not-for-credit';

export interface ISegmentProgressProps {
    segments: LaunchSegment[];
    currentSegmentItemSk?: number;
    timeRecords: CleTimeRecord[];
    onSelectSegment: (segment: LaunchSegment) => void;
    playbackProgress?: PlaybackPosition[];
}

const statusLabels: Record<SegmentProgressStatus, string> = {
    complete: 'credit tracking complete',
    'in-progress': 'credit tracking in progress',
    'not-started': 'credit tracking not started',
    'not-for-credit': 'not for credit',
};

export const findMatchingTimeRecord = (
    segment: LaunchSegment,
    timeRecords: CleTimeRecord[],
) => timeRecords.find(timeRecord => {
    const matchesItem = segment.itemSk !== undefined && timeRecord.item_SK === segment.itemSk;
    const matchesProgramSegment = segment.prgSegmentSk !== undefined &&
        timeRecord.prgSegment_SK === segment.prgSegmentSk;

    return !timeRecord.isLive && (matchesItem || matchesProgramSegment);
});

export const getSegmentProgressStatus = (
    segment: LaunchSegment,
    timeRecords: CleTimeRecord[],
    resolvedPercentComplete?: number,
): SegmentProgressStatus => {
    const record = findMatchingTimeRecord(segment, timeRecords);

    if (record?.trackCle === false)
        return 'not-for-credit';

    const percentComplete = resolvedPercentComplete ?? record?.percentComplete ?? 0;
    if (percentComplete >= 100)
        return 'complete';
    if (percentComplete > 0)
        return 'in-progress';

    return 'not-started';
};

interface ISegmentProgressItemProps {
    segment: LaunchSegment;
    segmentNumber: number;
    isCurrent: boolean;
    timeRecords: CleTimeRecord[];
    playbackProgress?: PlaybackPosition;
    buttonRef: (element: HTMLButtonElement | null) => void;
    onSelectSegment: (segment: LaunchSegment) => void;
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

const SegmentProgressItem = ({
    segment,
    segmentNumber,
    isCurrent,
    timeRecords,
    playbackProgress,
    buttonRef,
    onSelectSegment,
    onKeyDown,
}: ISegmentProgressItemProps) => {
    const record = findMatchingTimeRecord(segment, timeRecords);
    const percentComplete = getOnDemandCreditPercentComplete(record?.percentComplete, playbackProgress);
    const status = getSegmentProgressStatus(segment, timeRecords, percentComplete);
    const circleRadius = 10.5;
    const circleCircumference = 2 * Math.PI * circleRadius;

    return (
        <li className={`segment-progress__item segment-progress__item--${status}`}>
            <button
                ref={buttonRef}
                className='segment-progress__button'
                type='button'
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Segment ${segmentNumber}, ${status === 'in-progress' ? `${percentComplete}% ` : ''}${statusLabels[status]}${isCurrent ? ', current segment' : ''}`}
                onClick={() => onSelectSegment(segment)}
                onKeyDown={onKeyDown}
                disabled={!segment.itemSk}
            >
                <span className='segment-progress__marker' aria-hidden='true'>
                    {status === 'complete' &&
                        <svg className='segment-progress__checkmark' viewBox='0 0 24 24'>
                            <path d='M7.5 11.7273L10.9412 15L17.25 9' />
                        </svg>
                    }
                    {status === 'in-progress' &&
                        <svg className='segment-progress__progress-ring' viewBox='0 0 24 24'>
                            <circle cx='12' cy='12' r={circleRadius} className='segment-progress__progress-ring-track' />
                            <circle
                                cx='12'
                                cy='12'
                                r={circleRadius}
                                className='segment-progress__progress-ring-value'
                                strokeDasharray={`${(percentComplete * circleCircumference) / 100} ${circleCircumference}`}
                            />
                        </svg>
                    }
                    {(status === 'in-progress' || status === 'not-started') && <span className='segment-progress__play-icon' />}
                </span>
                <span className='segment-progress__label'>
                    <span>Segment {segmentNumber}</span>
                    {status === 'not-for-credit' && <span>(non-credit)</span>}
                </span>
            </button>
        </li>
    );
};

export const SegmentProgress = ({
    segments,
    currentSegmentItemSk,
    timeRecords,
    onSelectSegment,
    playbackProgress = [],
}: ISegmentProgressProps) => {
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const currentSegmentIndex = segments.findIndex(segment => segment.itemSk === currentSegmentItemSk);
    const playbackProgressByItemSk = useMemo(() => {
        const progressByItemSk = new Map<number, PlaybackPosition>();

        playbackProgress.forEach(progress => {
            if (!progressByItemSk.has(progress.itemSk))
                progressByItemSk.set(progress.itemSk, progress);
        });

        return progressByItemSk;
    }, [playbackProgress]);

    useEffect(() => {
        const currentButton = buttonRefs.current[currentSegmentIndex];
        currentButton?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
    }, [currentSegmentIndex, segments.length]);

    const focusSegment = (index: number) => {
        const button = buttonRefs.current[index];
        button?.focus();
        button?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
    };

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        let nextIndex: number | undefined;

        switch (event.key) {
            case 'ArrowLeft':
                nextIndex = Math.max(0, index - 1);
                break;
            case 'ArrowRight':
                nextIndex = Math.min(segments.length - 1, index + 1);
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = segments.length - 1;
                break;
        }

        if (nextIndex !== undefined) {
            event.preventDefault();
            focusSegment(nextIndex);
        }
    };

    return (
        <nav className='segment-progress' aria-label='Program segment progress'>
            <div className='segment-progress__scroller'>
                <ol className={`segment-progress__list ${segments.length <= 6 ? 'segment-progress__list--fit' : ''}`}>
                    {segments.map((segment, index) => {
                        const isCurrent = index === currentSegmentIndex;
                        const segmentNumber = index + 1;

                        return (
                            <SegmentProgressItem
                                key={segment.itemSk ?? segment.prgSegmentSk ?? segmentNumber}
                                segment={segment}
                                segmentNumber={segmentNumber}
                                isCurrent={isCurrent}
                                timeRecords={timeRecords}
                                playbackProgress={segment.itemSk === undefined ? undefined : playbackProgressByItemSk.get(segment.itemSk)}
                                buttonRef={element => buttonRefs.current[index] = element}
                                onSelectSegment={onSelectSegment}
                                onKeyDown={event => onKeyDown(event, index)}
                            />
                        );
                    })}
                </ol>
            </div>
        </nav>
    );
};

export default SegmentProgress;
